import { View } from '@kit/ui'
import {
  createRootRouteWithContext,
  Outlet,
  useLayoutEffect,
  useRouter,
} from '@tanstack/react-router'
import { accessActions, IRouteNode } from '@web/app/features/access'
import { RouterContext } from '@web/app/provider'
import { useDispatch, useSelector } from 'react-redux'

// 💡 Selectores y Hooks necesarios
import { useGetPermissionsMeQuery } from '@web/app/features/access/store/accessApi'
import { authSelectors } from '@web/app/features/auth'

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
})

const formatLabel = (name: string) => {
  const parts = name.split('.');
  const label = parts.length > 1 ? parts[1] : parts[0];
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function RootComponent() {
  const router = useRouter()
  const dispatch = useDispatch()
  
  // 1. Observamos si el usuario está autenticado
  const isAuthenticated = useSelector(authSelectors.isAuthenticated)
  
  // 2. Hook de API con RECARGA FORZADA
  const { data: apiData, isLoading } = useGetPermissionsMeQuery(undefined, {
    skip: !isAuthenticated,
    refetchOnMountOrArgChange: true 
  })

  useLayoutEffect(() => {
    // 3. Si se cerró sesión, vaciamos el sidebar de inmediato
    if (!isAuthenticated) {
      console.log("🚪 Usuario deslogueado: Limpiando Sidebar")
      dispatch(accessActions.setRoutes([]))
      return
    }

    if (isLoading || !apiData?.data) return

    
    const allPhysicalRoutes = (router.routeTree.children as unknown as IRouteNode[]) || []
    const indexNode = allPhysicalRoutes.find(c => c.id === '/_index')
    
    if (!indexNode || !indexNode.children) {
      console.error("No se encontró el nodo /_index en el routeTree")
      return
    }

    const authorizedTree = resolveAndFilter(indexNode.children, apiData.data)

    dispatch(accessActions.setRoutes(authorizedTree))
    

  }, [router, apiData, isLoading, dispatch, isAuthenticated])

  return (
    <View f={1} theme={'blue'}>
      <Outlet />
    </View>
  )
}

function resolveAndFilter(physicalRoutes: IRouteNode[], apiModules: any[]): IRouteNode[] {
  const finalTree: any[] = []

  // --- A. DASHBOARD ---
  const dashboard = physicalRoutes.find(r => r.id === '/_index/dashboard')
  if (dashboard) {
    const dashIcon = dashboard.options?.staticData?.metaRoute?.icon || 'LayoutDashboard';
    finalTree.push({
      id: dashboard.id,
      to: dashboard.to,
      level: 1,
      options: { 
        staticData: { 
          metaRoute: { title: 'Dashboard', icon: dashIcon } 
        } 
      }
    })
  }

  // --- B. MAPEADO POR API ---
  apiModules.forEach(apiModule => {
    const modulePath = `/_index${apiModule.href}`
    const physicalModule = physicalRoutes.find(r => r.id === modulePath)

    if (modulePath.includes('/cuenta')) return

    if (physicalModule && apiModule.enabled) {
      const authorizedChildren: any[] = []

      apiModule.child_feature_list?.forEach((apiRes: any) => {
        const resourcePath = `/_index${apiRes.href}`
        const physicalRes = physicalModule.children?.find(r => r.id === resourcePath)

        if (physicalRes && apiRes.enabled) {
          const childTitle = physicalRes.options?.staticData?.metaRoute?.title || formatLabel(apiRes.name)
          authorizedChildren.push({
            id: physicalRes.id,
            to: physicalRes.to,
            level: 2,
            options: {
              staticData: {
                metaRoute: {
                  title: childTitle,
                  icon: apiRes.icon,
                }
              }
            }
          })
        }
      })

        const parentTitle = physicalModule.options?.staticData?.metaRoute?.title || apiModule.name

        finalTree.push({
          id: physicalModule.id,
          to: physicalModule.to,
          level: 1,
          options: {
            staticData: {
              metaRoute: {
                title: parentTitle,
                icon: apiModule.icon,
              }
            }
          },
          children: authorizedChildren,
        })
    }
  })

  return finalTree
}